import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Tooltip, Collapse, Skeleton,
  TextField, InputAdornment, Paper, Divider, Button, MenuItem, Grid, Card
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import { fetchRecords } from '../services/api';

const fmt = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

// Auto-calculate closing balance dynamically for display
const calcClosing = (e) => {
  return (Number(e.opening_balance) || 0) + (Number(e.accrued) || 0)
       - (Number(e.availed) || 0) - (Number(e.encashed) || 0) - (Number(e.lapsed) || 0);
};

export default function LeaveBalanceGrouped({ records, onEdit, onDelete, onAdd, onView }) {
  const [expanded, setExpanded]       = useState(null);
  const [search, setSearch]           = useState('');
  const [sortParam, setSortParam]     = useState('name');
  
  // Filters
  const [filterFY, setFilterFY]       = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterBG, setFilterBG]       = useState('');
  const [filterType, setFilterType]   = useState('');

  const [assignments, setAssignments] = useState([]);
  const [persons, setPersons]         = useState([]);
  const [absenceTypes, setAbsenceTypes] = useState([]);
  const [companies, setCompanies]     = useState([]);
  const [bgs, setBgs]                 = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    Promise.all([
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } })),
      fetchRecords('ABSENCE_TYPE').catch(() => ({ data: { data: [] } })),
      fetchRecords('COMPANY').catch(() => ({ data: { data: [] } })),
      fetchRecords('BUSINESS_GROUP').catch(() => ({ data: { data: [] } })),
    ]).then(([a, p, ab, c, bg]) => {
      setAssignments(a.data.data || []);
      setPersons(p.data.data || []);
      setAbsenceTypes(ab.data.data || []);
      setCompanies(c.data.data || []);
      setBgs(bg.data.data || []);
      setLoading(false);
    });
  }, []);

  const assignmentMap = useMemo(() =>
    Object.fromEntries(assignments.map(a => [a.assignment_id, a])), [assignments]);
  const personMap = useMemo(() =>
    Object.fromEntries(persons.map(p => [p.person_id, p])), [persons]);
  const absenceTypeMap = useMemo(() =>
    Object.fromEntries(absenceTypes.map(t => [t.absence_type_id, t])), [absenceTypes]);

  // Options for filters
  const fYears = useMemo(() => [...new Set(records.map(r => r.fiscal_year).filter(Boolean))], [records]);
  const aTypes = useMemo(() => [...new Set(records.map(r => r.absence_type_id).filter(Boolean))], [records]);
  
  // Group entries by assignment_id + fiscal_year
  const groups = useMemo(() => {
    const map = {};
    for (const e of records) {
      const k = `${e.assignment_id || '—'}_${e.fiscal_year || '—'}`;
      if (!map[k]) map[k] = [];
      map[k].push(e);
    }
    
    return Object.entries(map)
      .map(([key, items]) => {
        const asnId = items[0].assignment_id || '—';
        const fy = items[0].fiscal_year || '—';
        const asn = assignmentMap[asnId];
        const person = asn ? personMap[asn.person_id] : (items[0].person_id ? personMap[items[0].person_id] : null);
        const fullName = person ? [person.first_name, person.last_name].filter(Boolean).join(' ') : null;
        
        let totalAvailable = 0;
        items.forEach(i => totalAvailable += calcClosing(i));
        
        return {
          key,
          assignment_id: asnId,
          assignment: asn,
          person_id: items[0].person_id || (asn ? asn.person_id : '—'),
          person,
          fullName: fullName || (asn ? asn.employee_code : 'Unknown employee'),
          fiscal_year: fy,
          company_id: items[0].COMPANY_id,
          bg_id: items[0].bg_id,
          entries: items,
          totalAvailable,
        };
      });
  }, [records, assignmentMap, personMap]);

  // Apply Search, Filters, and Sorting
  const filteredAndSorted = useMemo(() => {
    let result = groups;

    // Filters
    if (filterFY) result = result.filter(g => g.fiscal_year === filterFY);
    if (filterCompany) result = result.filter(g => g.company_id === filterCompany);
    if (filterBG) result = result.filter(g => g.bg_id === filterBG);
    
    // Type Filter (filter entries within group, and drop empty groups)
    if (filterType) {
      result = result.map(g => ({
        ...g,
        entries: g.entries.filter(e => e.absence_type_id === filterType)
      })).filter(g => g.entries.length > 0);
    }

    // Search
    const q = search.trim().toLowerCase();
    if (q) {
      result = result.map(g => {
        const groupHit =
          g.assignment_id.toLowerCase().includes(q) ||
          g.person_id.toLowerCase().includes(q) ||
          (g.fullName || '').toLowerCase().includes(q);
          
        if (groupHit) return g;
        
        const matchingEntries = g.entries.filter(e => {
          const ab = absenceTypeMap[e.absence_type_id];
          return (
            (e.absence_type_id || '').toLowerCase().includes(q) ||
            (ab?.absence_name || '').toLowerCase().includes(q)
          );
        });
        return matchingEntries.length ? { ...g, entries: matchingEntries } : null;
      }).filter(Boolean);
    }

    // Sort
    result.sort((a, b) => {
      if (sortParam === 'name') return (a.fullName || '').localeCompare(b.fullName || '');
      if (sortParam === 'person_id') return a.person_id.localeCompare(b.person_id);
      if (sortParam === 'fy') return b.fiscal_year.localeCompare(a.fiscal_year); // newest FY first
      return 0;
    });

    return result;
  }, [groups, search, filterFY, filterCompany, filterBG, filterType, sortParam, absenceTypeMap]);

  const totalEntries = filteredAndSorted.reduce((s, g) => s + g.entries.length, 0);

  if (loading) {
    return (
      <Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={56} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        
        {/* Top Row: Search & Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by Person ID, Name, Assignment or Leave Type…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 400 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white', fontSize: 13, borderRadius: '10px',
                '& fieldset': { borderColor: '#E8EDF5', borderWidth: '1.5px' },
                '&:hover fieldset': { borderColor: '#bbf7d0' },
                '&.Mui-focused fieldset': { borderColor: '#22c55e', borderWidth: '2px' },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94A3B8', fontSize: 17 }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Stack direction="row" spacing={1.5}>
            <Stack direction="row" alignItems="center" gap={0.6}
              sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10B981' }} />
              <Typography fontSize={12} fontWeight={600} color="text.secondary">
                {filteredAndSorted.length} employees
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.6}
              sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
              <Typography fontSize={12} fontWeight={600} color="text.secondary">
                {totalEntries} leave records
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Bottom Row: Filters & Sorting */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
          <FilterListIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
          
          <TextField select size="small" value={filterFY} onChange={e => setFilterFY(e.target.value)}
            sx={{ width: 140, '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: '8px' } }}
            InputProps={{ sx: { fontSize: 13 } }}>
            <MenuItem value="">All FY</MenuItem>
            {fYears.map(fy => <MenuItem key={fy} value={fy}>{fy}</MenuItem>)}
          </TextField>

          <TextField select size="small" value={filterType} onChange={e => setFilterType(e.target.value)}
            sx={{ width: 160, '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: '8px' } }}
            InputProps={{ sx: { fontSize: 13 } }}>
            <MenuItem value="">All Leave Types</MenuItem>
            {aTypes.map(t => (
              <MenuItem key={t} value={t}>{absenceTypeMap[t]?.absence_name || t}</MenuItem>
            ))}
          </TextField>
          
          <TextField select size="small" value={filterCompany} onChange={e => setFilterCompany(e.target.value)}
            sx={{ width: 160, '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: '8px' } }}
            InputProps={{ sx: { fontSize: 13 } }}>
            <MenuItem value="">All Companies</MenuItem>
            {companies.map(c => <MenuItem key={c.company_id} value={c.company_id}>{c.company_name}</MenuItem>)}
          </TextField>

          <Box sx={{ flex: 1 }} />
          
          <SortIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
          <TextField select size="small" value={sortParam} onChange={e => setSortParam(e.target.value)}
            sx={{ width: 160, '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: '8px' } }}
            InputProps={{ sx: { fontSize: 13 } }}>
            <MenuItem value="name">Sort by Name</MenuItem>
            <MenuItem value="person_id">Sort by Person ID</MenuItem>
            <MenuItem value="fy">Sort by Fiscal Year</MenuItem>
          </TextField>
        </Stack>
      </Box>

      {/* Employee Cards */}
      <Stack spacing={2}>
        {filteredAndSorted.length === 0 ? (
          <Paper sx={{ py: 8, textAlign: 'center', bgcolor: 'white', border: '1.5px solid #E5E7EB', borderRadius: '12px' }}>
            <Stack alignItems="center" gap={1.5}>
              <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: '#F3F4F6',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InboxIcon sx={{ fontSize: 26, color: '#9CA3AF' }} />
              </Box>
              <Typography fontWeight={600} fontSize={14} color="#374151">
                No Leave Balance Records Found
              </Typography>
              <Typography fontSize={12.5} color="#9CA3AF">
                Create a Leave Balance to start tracking employee leave.
              </Typography>
              {onAdd && (
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => onAdd()}
                  sx={{ mt: 1, borderRadius: '8px', color: '#16a34a', borderColor: '#22c55e', '&:hover': { bgcolor: '#f0fdf4' } }}>
                  Add Record
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          filteredAndSorted.map((g) => {
            const isOpen = expanded === g.key;
            return (
              <Card key={g.key} elevation={0} sx={{ 
                border: '1.5px solid', borderColor: isOpen ? '#22c55e' : '#E5E7EB',
                borderRadius: '12px', overflow: 'visible',
                transition: 'border-color 0.2s',
                boxShadow: isOpen ? '0 4px 20px rgba(34,197,94,0.1)' : 'none'
              }}>
                {/* Header (Collapsible) */}
                <Box
                  onClick={() => setExpanded(isOpen ? null : g.key)}
                  sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                    px: 2.5, py: 2, cursor: 'pointer',
                    bgcolor: isOpen ? '#F0FDF4' : 'white', borderRadius: '12px',
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: isOpen ? '#F0FDF4' : '#F9FAFB' },
                  }}
                >
                  <Box sx={{
                    width: 42, height: 42, borderRadius: '12px',
                    background: 'linear-gradient(135deg,#16a34a,#22c55e)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <PersonIcon sx={{ fontSize: 22, color: 'white' }} />
                  </Box>
                  
                  <Box sx={{ minWidth: 200, flex: 1 }}>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography fontSize={12} fontWeight={700} color="#64748B" sx={{ fontFamily: 'monospace' }}>
                        {g.person_id}
                      </Typography>
                      <Typography fontSize={12} color="#CBD5E1">|</Typography>
                      <Typography fontSize={15} fontWeight={700} color="#111827">
                        {g.fullName}
                      </Typography>
                      <Typography fontSize={12} color="#CBD5E1">|</Typography>
                      <Typography fontSize={12} fontWeight={600} color="#64748B" sx={{ fontFamily: 'monospace' }}>
                        {g.assignment_id}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <Box textAlign="center">
                      <Typography fontSize={11} color="#9CA3AF" textTransform="uppercase">Fiscal Year</Typography>
                      <Typography fontSize={13} fontWeight={600} color="#374151">{g.fiscal_year}</Typography>
                    </Box>
                    <Box textAlign="center">
                      <Typography fontSize={11} color="#9CA3AF" textTransform="uppercase">Leave Types</Typography>
                      <Typography fontSize={13} fontWeight={600} color="#374151">{g.entries.length} Types</Typography>
                    </Box>
                    <Box textAlign="right" sx={{ minWidth: 100 }}>
                      <Typography fontSize={11} color="#16a34a" textTransform="uppercase" fontWeight={600}>Total Available</Typography>
                      <Typography fontSize={16} fontWeight={800} color="#16a34a">{fmt(g.totalAvailable)} Days</Typography>
                    </Box>
                  </Box>

                  <IconButton size="small" sx={{
                    width: 32, height: 32, borderRadius: '8px',
                    color: isOpen ? '#16a34a' : '#6B7280',
                    border: '1.5px solid #E5E7EB',
                    bgcolor: isOpen ? '#f0fdf4' : 'white',
                    '&:hover': { bgcolor: '#f0fdf4', borderColor: '#22c55e', color: '#16a34a' },
                  }}>
                    {isOpen ? <KeyboardArrowUpIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Box>

                {/* Expanded Content */}
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ bgcolor: '#FAFBFC', borderTop: '1.5px solid #E5E7EB', p: 2.5, borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                    <Grid container spacing={2}>
                      {g.entries.map((e, ei) => {
                        const ab = absenceTypeMap[e.absence_type_id] || {};
                        const closing = calcClosing(e);
                        return (
                          <Grid item xs={12} md={6} lg={4} key={e.leave_bal_id || ei}>
                            <Card variant="outlined" sx={{ 
                              p: 2, borderRadius: '10px', borderColor: '#E5E7EB',
                              '&:hover': { borderColor: '#22c55e', boxShadow: '0 2px 8px rgba(34,197,94,0.08)' }
                            }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                <Box>
                                  <Typography fontSize={14} fontWeight={700} color="#111827">
                                    {ab.absence_name || e.absence_type_id}
                                  </Typography>
                                  <Typography fontSize={11} color="#6B7280" sx={{ fontFamily: 'monospace' }}>
                                    {e.absence_type_id}
                                  </Typography>
                                </Box>
                                
                                <Stack direction="row" spacing={0.5}>
                                  {onView && (
                                    <Tooltip title="View" arrow placement="top">
                                      <IconButton size="small" onClick={() => onView(e)}
                                        sx={{ width: 26, height: 26, borderRadius: '6px', color: '#6B7280', bgcolor: '#F3F4F6', '&:hover': { bgcolor: '#E5E7EB' } }}>
                                        <VisibilityIcon sx={{ fontSize: 13 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {onEdit && (
                                    <Tooltip title="Edit" arrow placement="top">
                                      <IconButton size="small" onClick={() => onEdit(e)}
                                        sx={{ width: 26, height: 26, borderRadius: '6px', color: '#22c55e', bgcolor: '#F0FDF4', '&:hover': { bgcolor: '#DCFCE7' } }}>
                                        <EditIcon sx={{ fontSize: 13 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {onDelete && (
                                    <Tooltip title="Delete" arrow placement="top">
                                      <IconButton size="small" onClick={() => onDelete(e)}
                                        sx={{ width: 26, height: 26, borderRadius: '6px', color: '#EF4444', bgcolor: '#FEF2F2', '&:hover': { bgcolor: '#FEE2E2' } }}>
                                        <DeleteIcon sx={{ fontSize: 13 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </Box>
                              
                              <Stack spacing={0.8} sx={{ mb: 1.5 }}>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography fontSize={12} color="#64748B">Opening Balance :</Typography>
                                  <Typography fontSize={12} fontWeight={600} color="#374151">{fmt(e.opening_balance)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography fontSize={12} color="#64748B">Accrued :</Typography>
                                  <Typography fontSize={12} fontWeight={600} color="#16a34a">+{fmt(e.accrued)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography fontSize={12} color="#64748B">Availed :</Typography>
                                  <Typography fontSize={12} fontWeight={600} color="#EF4444">-{fmt(e.availed)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography fontSize={12} color="#64748B">Encashed :</Typography>
                                  <Typography fontSize={12} fontWeight={600} color="#F59E0B">-{fmt(e.encashed)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between">
                                  <Typography fontSize={12} color="#64748B">Lapsed :</Typography>
                                  <Typography fontSize={12} fontWeight={600} color="#6B7280">-{fmt(e.lapsed)}</Typography>
                                </Box>
                              </Stack>
                              
                              <Divider sx={{ my: 1 }} />
                              
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography fontSize={10} color="#9CA3AF">Closing Balance</Typography>
                                  <Typography fontSize={18} fontWeight={800} color="#111827">{fmt(closing)}</Typography>
                                </Box>
                                <Box textAlign="right">
                                  <Typography fontSize={10} color="#9CA3AF">Last Updated</Typography>
                                  <Typography fontSize={11} fontWeight={500} color="#6B7280">{e.last_updated ? new Date(e.last_updated).toLocaleDateString('en-GB') : '—'}</Typography>
                                </Box>
                              </Box>

                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Collapse>
              </Card>
            );
          })
        )}
      </Stack>
    </Box>
  );
}
